import { apiRequest, graphConfig, loginRequest } from '../components/Auth/authConfig';
import { msalInstance } from '../components/Auth/MsalProvider';
import { VoucherFormState } from '../components/VoucherForm/Context';
import { GetVouchersResponse } from '../types/GetVouchers';
import { GetBatchesResponse } from '../types/GetBatches';
import { Batch, Voucher } from '../types/Voucher';
import { VoucherComment } from '../types/Comments';
import { Line } from '../types/Line';
import { City } from '../types/City';
import { AllowedRelationsRule, ForbiddenRelationsRule, Rule } from '../types/Rules';
import { VoucherFilter } from '../types/VoucherFilter';
import { PopupRequest, InteractionRequiredAuthError } from '@azure/msal-browser';
import { BatchUpdatePayload } from '../types/BatchUpdate';
import { VoucherLinkedOrder } from '../types/VoucherLinkedOrder';
import { CancelVouchersByCSV, CancelVouchersByCSVStatus } from '../types/CancelVouchers';
import { VoucherActionLog } from '../types/VoucherActionLog';
import { Brand } from '../types/Brand';

const VOUCHER_SERVICE_URL =
  process.env.REACT_APP_VOUCHER_SERVICE_API_BASE_URL || 'http://localhost:8081';
const CMS_GRAPHQL_URL = 'https://api.cms.flixbus.com/gql';

export async function getBackendApiToken(requestType: PopupRequest) {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error('No active account. Please sign in first.');

  try {
    const response = await msalInstance.acquireTokenSilent({ ...requestType, account });
    return response.accessToken;
  } catch (error: unknown) {
    // Check if error is InteractionRequiredAuthError by instance or error name/code
    const isInteractionRequired =
      error instanceof InteractionRequiredAuthError ||
      (error as { errorCode?: string })?.errorCode === 'interaction_required' ||
      (error as { name?: string })?.name === 'InteractionRequiredAuthError';

    if (isInteractionRequired) {
      // Session expired or interaction required - fall back to interactive authentication
      try {
        const response = await msalInstance.acquireTokenPopup(requestType);
        return response.accessToken;
      } catch (popupError: unknown) {
        // If popup fails, try redirect as last resort
        await msalInstance.acquireTokenRedirect(requestType);
        throw new Error('Redirecting to login...');
      }
    }
    throw error;
  }
}

async function fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
  const token = await getBackendApiToken(apiRequest);
  const headers = new Headers({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...options.headers,
  });

  return fetch(url, { ...options, headers });
}

async function fetchWithAuthForm(url: string, options: RequestInit): Promise<Response> {
  const token = await getBackendApiToken(apiRequest);
  const headers = new Headers({
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  });

  return fetch(url, { ...options, headers });
}

export async function callMsGraph() {
  const token = await getBackendApiToken(loginRequest);

  return fetch(graphConfig.graphMeEndpoint, { headers: { Authorization: `Bearer ${token}` } })
    .then(response => response.json())
    .catch(console.error); // eslint-disable-line no-console
}

export async function getVouchers(filters: VoucherFilter): Promise<GetVouchersResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.code) params.append('code', filters.code);
  if (filters.createdBy) params.append('createdBy', filters.createdBy);
  if (filters.status) params.append('status', filters.status);

  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/vouchers?${params}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getVoucher(id: string): Promise<Voucher> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher-by-id/${id}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

async function requestWithAuth<T, P = undefined>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  payload?: P, // Generic payload type
): Promise<T> {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (payload && ['POST', 'PUT', 'PATCH'].includes(method)) {
    options.body = JSON.stringify(payload);
  }

  const response = await fetchWithAuth(url, options);
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

type RelationRule = AllowedRelationsRule | ForbiddenRelationsRule;

type SerializedRelationRule = RelationRule & {
  from: string[];
  to: string[];
};

type SerializedRule = Exclude<Rule, RelationRule> | SerializedRelationRule;

type SerializedPayload = Omit<VoucherFormState['payload'], 'rules'> & {
  rules: SerializedRule[];
};

function serializeRelationRules(payload: VoucherFormState['payload']): SerializedPayload {
  const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

  const rest: VoucherFormState['payload'] = { ...payload };
  // The backend only permits maxUsageCount when multiUsage is true.
  if (!rest.multiUsage) {
    delete rest.maxUsageCount;
  }

  return {
    ...rest,
    rules: rest.rules.map(rule => {
      if (rule.type === 'allowed_relations' || rule.type === 'forbidden_relations') {
        return {
          ...rule,
          from: unique(rule.relations.map(relation => relation.from)),
          to: unique(rule.relations.map(relation => relation.to)),
        };
      }
      return rule;
    }),
  };
}

export const generateVoucher = (payload: VoucherFormState['payload']) =>
  requestWithAuth<Voucher, SerializedPayload>(
    `${VOUCHER_SERVICE_URL}/api/voucher/generate`,
    'POST',
    serializeRelationRules(payload),
  );

export const generateMultipleVouchers = (payload: VoucherFormState['payload']) =>
  requestWithAuth<Voucher, SerializedPayload>(
    `${VOUCHER_SERVICE_URL}/api/voucher/generate-multiple`,
    'POST',
    serializeRelationRules(payload),
  );

export const fetchVoucherBatch = (payload: BatchUpdatePayload) =>
  requestWithAuth<Voucher, BatchUpdatePayload>(
    `${VOUCHER_SERVICE_URL}/api/voucher/batch`,
    'PUT',
    payload,
  );

// Returns the current user's batches, sorted newest-first and paginated
// server-side via page/limit (defaults: page 1, limit 10). `count` in the
// response is the grand total. The endpoint takes no prefix/status filter params.
export async function getBatches(page = 1, limit = 10): Promise<GetBatchesResponse> {
  const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });

  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/batches?${params}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getBatch(id: string): Promise<Batch> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/batch/${id}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getBatchDownloadData(id: string): Promise<string> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/batch/${id}/export`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/csv',
      Accept: 'text/csv',
    },
  });
  if (!response.ok) throw new Error(await response.text());

  return response.text();
}

export async function getLines(q: string): Promise<Line[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/lines/autocomplete?q=${q}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getLineById(lineId: string): Promise<Line> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/lines/${lineId}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

const CITIES_CACHE_KEY = 'voucher-portal:cities-catalog';

let citiesCatalog: Promise<City[]> | null = null;

function readCitiesFromSession(): City[] | null {
  try {
    const cached = sessionStorage.getItem(CITIES_CACHE_KEY);
    return cached ? (JSON.parse(cached) as City[]) : null;
  } catch {
    return null;
  }
}

function writeCitiesToSession(cities: City[]): void {
  try {
    sessionStorage.setItem(CITIES_CACHE_KEY, JSON.stringify(cities));
  } catch {
    // Ignore storage errors (e.g. quota exceeded or unavailable storage).
  }
}

function fetchCitiesCatalog(): Promise<City[]> {
  if (citiesCatalog) return citiesCatalog;

  const cached = readCitiesFromSession();
  if (cached) {
    citiesCatalog = Promise.resolve(cached);
    return citiesCatalog;
  }

  citiesCatalog = fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/cities`, {
    method: 'GET',
  })
    .then(async response => {
      if (!response.ok) throw new Error(await response.text());
      const cities = (await response.json()) as City[];
      writeCitiesToSession(cities);
      return cities;
    })
    .catch(error => {
      citiesCatalog = null;
      throw error;
    });

  return citiesCatalog;
}

export async function getCities(q: string, limit?: number): Promise<City[]> {
  const catalog = await fetchCitiesCatalog();
  const trimmed = q.trim().toLowerCase();

  const filtered = trimmed
    ? catalog
        .filter(city => city.name.toLowerCase().includes(trimmed))
        // Prefix matches first, then alphabetical for a predictable order.
        .sort((a, b) => {
          const aPrefix = a.name.toLowerCase().startsWith(trimmed) ? 0 : 1;
          const bPrefix = b.name.toLowerCase().startsWith(trimmed) ? 0 : 1;
          return aPrefix - bPrefix || a.name.localeCompare(b.name);
        })
    : catalog;

  return limit !== undefined ? filtered.slice(0, limit) : filtered;
}

export async function getCityById(cityId: string): Promise<City> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/cities/${cityId}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function unmaskVoucherCode(id: string): Promise<{ voucherCode: string }> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/unmask-code`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getLinkedOrders(id: string): Promise<VoucherLinkedOrder[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/orders`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export const saveComment = (voucherId: Voucher['id'], payload: { comment: string }) =>
  requestWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${voucherId}/comment`, 'POST', payload);

export async function getVoucherActionLogs(id: string): Promise<VoucherActionLog[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/action-logs`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getVoucherComments(id: string): Promise<VoucherComment[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/comments`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function updateVoucherComment(payload: {
  voucherId: string;
  comment: string;
  commentId?: string;
}): Promise<VoucherComment> {
  const { voucherId, ...body } = payload;

  return requestWithAuth<VoucherComment, typeof body>(
    `${VOUCHER_SERVICE_URL}/api/voucher/${voucherId}/comment`,
    'POST',
    body,
  );
}

export async function cancelVouchersByFile(form: FormData): Promise<CancelVouchersByCSV> {
  const response = await fetchWithAuthForm(`${VOUCHER_SERVICE_URL}/api/voucher/file/cancel`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return response.json();
}

export async function getCancelVouchersByFileStatus(
  id: string,
): Promise<CancelVouchersByCSVStatus | { csv: string }> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/file/cancel/${id}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const contentType = response.headers.get('Content-Type') || '';

  if (contentType.includes('text/csv')) {
    return { csv: await response.text() };
  }

  try {
    return (await response.json()) as CancelVouchersByCSVStatus;
  } catch {
    return { csv: await response.text() };
  }
}

export async function getBrands(q = ''): Promise<Brand[]> {
  const query = `
    query {
      brands {
        uuid
        name
        type
        color
        colorLight
        colorDark
        status
      }
    }
  `;

  const response = await fetch(CMS_GRAPHQL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) throw new Error(await response.text());

  const result = await response.json();
  if (result.errors?.length) throw new Error(result.errors[0].message);
  if (!result.data?.brands) throw new Error('Unexpected response shape from CMS');
  const brands = result.data.brands;

  // Filter by search query if provided
  if (q) {
    const lowerQ = q.toLowerCase();
    return brands.filter(
      (b: Brand) => b.name.toLowerCase().includes(lowerQ) || b.uuid.toLowerCase().includes(lowerQ),
    );
  }

  return brands;
}
